import { useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthProvider";
import {
  HomeIcon,
  UsersIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { getFamilles } from "./familleAPI";
import { getSocietes } from "./societeAPI";
import SocieteCard from "./SocieteCard"; 
import FamilleSearch from "./FamilleSearch";
import FamilleCard from "./FamilleCard";
import FamilleDetail from "./DetailPage";

export default function Families() {
  const [families, setFamilies] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [search, setSearch] = useState("");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const famillesParPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dataFamilles, dataSocietes] = await Promise.all([
          getFamilles(),
          getSocietes(), // 🔸 AJOUTER
        ]);
        setFamilies(dataFamilles);
        setSocietes(dataSocietes); // 🔸 AJOUTER
      } catch (err) {
        console.error("Erreur lors de la récupération des données :", err);
        setError("Impossible de charger les données.");
      } finally {
        setLoading(false);
      }
    };    
    fetchData();
  }, []);

  return (
    <div className="min-h-[91vh] bg-white-50 p-6">
      <div className="bg-white p-8 shadow-2xl rounded-2xl max-w-full mx-auto w-full border border-gray-200 animate-fade-in h-[830px]">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <UserGroupIcon className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Gestion des familles</h1>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-transform hover:scale-105"
            >
              <HomeIcon className="w-5 h-5" />
              Accueil
            </button>
            <button
              onClick={() => navigate("/prospects/leads")}
              className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg shadow transition-transform hover:scale-105"
            >
              <UsersIcon className="w-5 h-5" />
              Leads
            </button>
          </div>
        </div>

        {/* Barre de recherche */}
        <FamilleSearch search={search} setSearch={setSearch} />

        {/* Contenu principal */}
        <div className="mt-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500 animate-pulse">
              <ArrowPathIcon className="w-10 h-10 mb-2 animate-spin text-blue-500" />
              Chargement des familles...
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-red-500">
              <ExclamationCircleIcon className="w-8 h-8 mb-2" />
              {error}
            </div>
          ) : families.length + societes.length === 0 ? (
            <div className="text-center text-gray-500 py-20">
              <UserGroupIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              Aucune famille n'est actuellement gérée.
            </div>
          ) : (
            <>
              {/* Pagination logic */}
              {(() => {
                const toutesEntites = [
                  ...families
                    .filter((f) => f.referent === user?.Nom)
                    .filter((f) =>
                      `${f.nom} ${f.prenom || ""}`.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((f) => ({ ...f, _type: "famille" })),
                  ...societes
                    .filter((s) => s.referent === user?.Nom)
                    .filter((s) =>
                      `${s.denomination || ""}`.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((s) => ({ ...s, _type: "societe" })),
                ];
                              
                const famillesParPage = 6;
                const totalPages = Math.ceil(toutesEntites.length / famillesParPage);
                const startIndex = (currentPage - 1) * famillesParPage;
                const currentEntites = toutesEntites.slice(startIndex, startIndex + famillesParPage);

                return (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-slide-up">
                      {currentEntites.map((entite) =>
                        entite._type === "famille" ? (
                          <FamilleCard
                            key={entite._id}
                            family={entite}
                            onClick={() => navigate(`/prospects/familles/${entite._id}`)}
                          />
                        ) : (
                          <SocieteCard
                            key={entite._id}
                            societe={entite}
                            onClick={() => navigate(`/prospects/familles/${entite._id}`)}
                          />
                        ))}
                    </div>

                    {/* Pagination buttons */}
                    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-8">
                      <div className="flex flex-1 justify-between sm:hidden">
                        <button
                          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Précédent
                        </button>
                        <button
                          onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Suivant
                        </button>
                      </div>
                      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <p className="text-sm text-gray-700">
                          Affichage de <span className="font-medium">{startIndex + 1}</span> à{" "}
                          <span className="font-medium">
                            {Math.min(startIndex + famillesParPage, toutesEntites.length)}
                          </span>{" "}
                          sur <span className="font-medium">{toutesEntites.length}</span> familles
                        </p>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                          >
                            <span className="sr-only">Précédent</span>
                            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                          </button>

                          {Array.from({ length: totalPages }).map((_, i) => (
                            <button
                              key={i}
                              onClick={() => setCurrentPage(i + 1)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0 ${
                                currentPage === i + 1
                                  ? "z-10 bg-blue-600 text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                  : "text-gray-900 hover:bg-gray-50"
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}

                          <button
                            onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                          >
                            <span className="sr-only">Suivant</span>
                            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
